<template>
  <div
    class="home w-full px-36 py-8 float-left flex justify-center items-center"
  >
    <!-- Done -->
    <div
      class="max-w-fix w-full flex flex-row float-left gap-14"
      style="max-width: 1248px"
    >
      <div class="w-3/5 py-9 px-10 float-left shadow rounded-lg flex-grow">
        <div class="flex flex-col gap-4 w-full float-left">
          <div class="w-full float-left">
            <h1 class="text-lg font-medium text-black">
              {{ $t("place-order.title") }}
            </h1>
            <h2 class="text-sm text-gray">
              {{ $t("place-order.sub-title") }}
            </h2>
          </div>
          <div class="w-full float-left input">
            <label
              :class="
                modelErrors.passenger_name ? 'text-primary-color' : 'text-gray'
              "
              >{{ $t("place-order.passenger_name") }}
              <span v-if="modelErrors.passenger_name">{{
                $t("errors.valid_passenger_name")
              }}</span></label
            >
            <input
              name="passenger_name"
              type="text"
              autocomplete="off"
              v-model="model.passenger_name"
              :placeholder="$t('place-order.passenger_name')"
            />
          </div>
          <div class="w-full float-left input">
            <label
              :class="
                modelErrors.passenger_phone_number
                  ? 'text-primary-color'
                  : 'text-gray'
              "
              >{{ $t("place-order.passenger_phone_number") }}
              <span v-if="modelErrors.passenger_phone_number">{{
                $t("errors.valid_passenger_phone_number")
              }}</span></label
            >
            <input
              name="passenger_phone_number"
              type="text"
              autocomplete="off"
              v-model="model.passenger_phone_number"
              :placeholder="$t('place-order.passenger_phone_number')"
            />
          </div>
          <div class="w-full float-left input">
            <label
              :class="
                modelErrors.passenger_email ? 'text-primary-color' : 'text-gray'
              "
              >{{ $t("place-order.passenger_email") }}
              <span v-if="modelErrors.passenger_email">{{
                $t("errors.valid_passenger_email")
              }}</span></label
            >
            <input
              name="passenger_email"
              type="text"
              autocomplete="off"
              v-model="model.passenger_email"
              :placeholder="$t('place-order.passenger_email')"
            />
          </div>
          <div class="w-full float-left input">
            <label class="text-gray">{{ $t("place-order.time-date") }}</label>
            <TimeSelectInput
              @input="selectedTime = $event"
              :value="selectedTime"
            />
          </div>
          <div class="w-full float-left input">
            <label
              :class="
                modelErrors.pick_up_location_id
                  ? 'text-primary-color'
                  : 'text-gray'
              "
              >{{ $t("place-order.pickup") }}
              <span v-if="modelErrors.pick_up_location_id">{{
                $t("errors.valid_pickup")
              }}</span></label
            >
            <GoogleMapsInput
              class="w-full"
              :placeholder="$t('place-order.select-pickup')"
              @inputSelected="pick_up_location_obj = $event"
              @validate="validate('pick_up_location_id')"
              :value="pick_up_location_obj"
            />
          </div>
          <div class="w-full float-left input">
            <label
              :class="
                modelErrors.pull_down_location_id
                  ? 'text-primary-color'
                  : 'text-gray'
              "
              >{{ $t("place-order.city") }}
              <span v-if="modelErrors.pull_down_location_id">{{
                $t("errors.valid_destination")
              }}</span></label
            >
            <GoogleMapsInput
              class="w-full"
              :placeholder="$t('place-order.city')"
              @inputSelected="pull_down_location_obj = $event"
              @validate="validate('pull_down_location_id')"
              :value="pull_down_location_obj"
            />
          </div>

          <div class="w-full float-left input">
            <label class="text-gray">{{ $t("place-order.voucher") }}</label>
            <input
              name="voucher"
              type="text"
              autocomplete="off"
              v-model="model.voucher"
              :placeholder="$t('place-order.voucher')"
            />
          </div>
          <br />
          <div style="width: 30%" class="float-left input">
            <div class="spring-spinner" v-if="servicesLoading">
              <div class="spring-spinner-part top">
                <div class="spring-spinner-rotator"></div>
              </div>
              <div class="spring-spinner-part bottom">
                <div class="spring-spinner-rotator"></div>
              </div>
            </div>
          </div>

          <div
            v-show="services.length > 0 && !servicesLoading"
            class="w-full float-left input"
          >
            <label
              :class="
                modelErrors.service_id ? 'text-primary-color' : 'text-gray'
              "
              >{{ $t("place-order.services") }}
              <span v-if="modelErrors.service_id">{{
                $t("errors.valid_service")
              }}</span></label
            >
            <RadioGroup
              :options="services"
              :value="selectedService"
              @selected_obj="selectedServiceObj = $event"
            />
          </div>

          <!-- Who pays this ride: the business account or the rider -->
          <div class="w-full float-left input">
            <label class="text-gray">{{ $t("business.pay_party") }}</label>
            <select v-model="model.payer" class="payer-select">
              <option value="BUSINESS">
                {{ $t("business.business_pays") }}
              </option>
              <option value="RIDER">{{ $t("business.rider_pays") }}</option>
            </select>
            <div
              v-if="model.payer === 'BUSINESS'"
              class="balance-hint"
              :class="{ 'text-primary-color': !businessActive }"
            >
              {{ $t("business.balance") }}:
              <strong
                >{{
                  businessBalance == null ? "—" : formatNum(businessBalance)
                }}
                {{ $t("business.syp") }}</strong
              >
              <span v-if="!businessActive">
                · {{ $t("business.disabled") }}</span
              >
            </div>
            <!-- Spend limits, kept visible so the user stays aware of the
                 per-order ceiling, daily ceiling and what is left for today. -->
            <div v-if="model.payer === 'BUSINESS'" class="limits-hint">
              <div>
                {{ $t("business.per_order_limit") }}:
                <strong>{{
                  businessMaxPerOrder == null
                    ? $t("business.no_limit")
                    : formatNum(businessMaxPerOrder) + " " + $t("business.syp")
                }}</strong>
              </div>
              <div>
                {{ $t("business.daily_limit") }}:
                <strong>{{
                  businessDailyLimit == null
                    ? $t("business.no_limit")
                    : formatNum(businessDailyLimit) + " " + $t("business.syp")
                }}</strong>
              </div>
              <div v-if="businessDailyLimit != null">
                {{ $t("business.daily_remaining") }}:
                <strong>{{
                  businessDailyRemaining == null
                    ? "—"
                    : formatNum(businessDailyRemaining) +
                      " " +
                      $t("business.syp")
                }}</strong>
              </div>
            </div>
          </div>

          <div
            v-if="selectedServiceObj && services.length > 0 && !servicesLoading"
            class="w-full float-left estimation"
          >
            <label class="text-gray">
              <p>
                {{ $t("place-order.estimated-price") }} :
                <span
                  class="old-price"
                  v-if="
                    selectedServiceObj.estimated_price !=
                    selectedServiceObj.to_pay
                  "
                  >{{ selectedServiceObj.estimated_price }}</span
                >
                <span>{{ selectedServiceObj.to_pay }}</span>
              </p>
            </label>
          </div>

          <div class="w-full float-left">
            <button
              class="mt-8 float-right btn"
              @click="sendRequest"
              :disabled="sendingRequest"
            >
              <div class="spring-spinner" v-if="sendingRequest">
                <div class="spring-spinner-part top">
                  <div class="spring-spinner-rotator"></div>
                </div>
                <div class="spring-spinner-part bottom">
                  <div class="spring-spinner-rotator"></div>
                </div>
              </div>
              <span v-else>
                {{ $t("place-order.send-orders") }}
              </span>
            </button>
          </div>
          <br />
        </div>
      </div>
      <div
        class="h-full rel flex flex-col gap-4 justify-start items-center sticky top-8"
        style="max-width: 400px"
      >
        <div
          class="bg-white shadow rounded-lg flex-grow h-auto pt-6 pb-0 px-4"
          style="width: 400px; height: 602px"
        >
          <Tabs ref="tabs" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectInput from "@/components/SelectInput.vue";
import TimeSelectInput from "@/components/TimeSelectInput.vue";
import RadioGroup from "@/components/RadioGroup.vue";
import CheckBoxesGroup from "@/components/CheckBoxesGroup.vue";
import Tabs from "@/components/Tabs.vue";
import GoogleMapsInput from "@/components/GoogleMapsInput.vue";

import * as yup from "yup";
import * as gjv from "geojson-validation";
import axios from "axios";
import moment from "moment";
import socketEventsMixin from "@/mixins/socketEventsMixin";
let CancelToken = axios.CancelToken;
var cancelRequest;

const modelSchema = yup.object().shape({
  service_id: yup.number().required(),
  pull_down_location_id: yup.number().required(),
  pick_up_location_id: yup.number().required(),
  passenger_name: yup.string().required(),
  passenger_phone_number: yup.string().required(),
  passenger_email: yup.string().email(),
});

export default {
  name: "PlaceOrder",
  title: "Dashboard",
  mixins: [socketEventsMixin],
  events() {
    return {
      "Passengers:send_notification": this.notify,
      //"Passengers:all_pending_bookings_count": this.allPendingBookingsCount,
      //"Passengers:passenger_pending_bookings_count": this.passengerPendingBookingsCount,
      // "Ride:captain_check": this.updateCounts,
      // "Passengers:card_payment_request": this.displayRequest,
    };
  },
  components: {
    SelectInput,
    TimeSelectInput,
    RadioGroup,
    CheckBoxesGroup,
    Tabs,
    GoogleMapsInput,
  },
  data() {
    return {
      serverURL: process.env.VUE_APP_BACK,
      pick_up_location_obj: null,
      pull_down_location_obj: null,
      lastOrderError: null,
      dynamicConstraints: [],
      services: [],
      servicesOriginal: [],
      servicesLoading: false,
      quoteDebounceTimer: null,
      model: {
        service_id: null,
        pick_up_location_id: null,
        pull_down_location_id: null,
        datetime: "ASAP",
        voucher: "",
        passenger_name: "",
        passenger_phone_number: "",
        passenger_email: "",
        payer: "BUSINESS",
      },
      businessBalance: null,
      businessActive: true,
      businessMaxPerOrder: null,
      businessDailyLimit: null,
      businessDailyRemaining: null,
      modelErrors: {
        service_id: false,
        pick_up_location_id: false,
        pull_down_location_id: false,
        passenger_name: false,
        passenger_phone_number: false,
        passenger_email: false,
      },
      errors: {
        multipleOrders: false,
      },
      multipleOrders: false,
      multipleOrdersCount: 2,
      selectedTime: "ASAP",
      cities: [],
      selectedCity: {
        centroid: {},
      },
      cars: [],

      langs: [],
      selectedCar: null,
      selectedService: "",
      selectedServiceObj: null,
      selectedCarObj: {},
      constraints: [],
      show_label: true,
      selectedConstraints: [],
      paymentMethods: [],
      selectedPaymentMethod: "",
      sendingRequest: false,
      allPendingBookings: 0,
      passengerPendingBookings: 0,
    };
  },
  mounted() {},
  created() {
    this.loadBusinessAccount();
  },
  beforeUnmount() {
    // A pending debounced quote (or in-flight request) must not fire error
    // popups or mutate state after the operator leaves the page.
    if (this.quoteDebounceTimer) clearTimeout(this.quoteDebounceTimer);
    if (cancelRequest) cancelRequest();
  },

  computed: {
    primaryLang() {
      if (this.$store.getters.selectedLanguage == "en") return "_en";
      else return "_ar";
    },
    secondaryLang() {
      if (this.$store.getters.selectedLanguage == "en") return "_ar";
      else return "_en";
    },
  },

  methods: {
    formatNum(v) {
      // Redenominated display (value / 100, 2 decimals) via the global money mixin.
      return this.formatMoney(v);
    },
    loadBusinessAccount() {
      this.axios
        .get(this.$store.getters.getRootAPI + "business-account")
        .then((res) => {
          const acc = (res.data && res.data.data) || {};
          this.businessBalance = acc.balance;
          this.businessActive = !!acc.active;
          // null = "no limit" for these ceilings; 0 is a real (blocking) limit.
          this.businessMaxPerOrder = acc.max_per_order;
          this.businessDailyLimit = acc.daily_limit;
          this.businessDailyRemaining = acc.daily_remaining;
        })
        .catch(() => {
          this.businessBalance = null;
          this.businessMaxPerOrder = null;
          this.businessDailyLimit = null;
          this.businessDailyRemaining = null;
          this.model.payer = "RIDER";
        });
    },
    notify({ booking_id, message }) {
      console.log("notified");

      this.$notify({
        title: "Order transformed",
        text: message,
        type: "success",
      });
      close();
      console.log("notified");
    },
    validate(field) {
      if (field in this.model) {
        modelSchema
          .validateAt(field, this.model)
          .then(() => {
            this.modelErrors[field] = false;
          })
          .catch(() => {
            this.modelErrors[field] = true;
          });
      }
    },

    fixScroll(n, count = 0, changeBy = 1) {
      let target =
        target > scrollLeft
          ? document.getElementById(`${n}`).offsetLeft + 12
          : document.getElementById(`${n}`).offsetLeft - 12;
      // let target = document.getElementById(`${n}`).offsetLeft;
      let scrollLeft = this.$refs.counts.scrollLeft;
      this.$refs.counts.scrollLeft =
        target > scrollLeft ? scrollLeft + changeBy : scrollLeft - changeBy;
      let diff = Math.floor(Math.abs(this.$refs.counts.scrollLeft - target));
      if (count < diff) {
        window.requestAnimationFrame(() => {
          this.fixScroll(n, count + 1, count + 1);
        });
      }
    },

    loadServices() {
      if (
        !this.model.pick_up_location_id ||
        !this.model.pull_down_location_id
      ) {
        return;
      }

      let data = {
        pickup: this.model.pick_up_location_id,
        destination: this.model.pull_down_location_id,
      };
      if (this.model.voucher) data.voucher = this.model.voucher;
      if (this.model.datetime != "ASAP") data.time = this.model.datetime;
      this.servicesLoading = true;

      if (cancelRequest) cancelRequest();
      let cancelled = false;
      axios
        .post(this.$store.getters.getRootAPI + "services", data, {
          cancelToken: new CancelToken(function executor(c) {
            cancelRequest = c;
          }),
        })
        .then((response) => {
          this.servicesOriginal = [];
          this.services = [];

          response.data.data.services.forEach((item) => {
            this.servicesOriginal.push({
              id: item.id,
              title_en: item.name_en,
              title_ar: item.name_ar,
              img: this.serverURL + item.icon,
              estimated_price: item.estimated_price,
              to_pay: item.to_pay,
              discounted_amount: item.discounted_amount,
            });
          });

          this.servicesOriginal.forEach((item) => {
            this.services.push({
              id: item.id,
              title: item["title" + this.primaryLang],
              img: item.img,
              estimated_price: item.estimated_price,
              to_pay: item.to_pay,
              discounted_amount: item.discounted_amount,
            });
          });

          if (this.services.length > 0) {
            this.selectedService = this.services[0].title;
            this.selectedServiceObj = this.services[0];
          }
        })
        .catch((error) => {
          // A request superseded by a newer keystroke lands here as a Cancel
          // with no `response`; reading response.data blindly threw and left
          // stale services/spinner state behind.
          if (axios.isCancel(error)) {
            cancelled = true;
            return;
          }
          console.log(error);
          const message =
            error.response &&
            error.response.data &&
            error.response.data.message;
          this.$notify({
            title: "Error",
            text: message || "Can't load srvices",
            type: "error",
          });
          this.services = [];
          this.selectedService = "";
          this.selectedServiceObj = null;
        })
        .finally(() => {
          // The newer in-flight request owns the spinner now.
          if (!cancelled) this.servicesLoading = false;
        });
    },

    // Typed fields (voucher) re-quote through this instead of calling
    // loadServices directly: one request after the operator pauses, instead
    // of one "invalid code" error per keystroke.
    debouncedLoadServices() {
      if (this.quoteDebounceTimer) clearTimeout(this.quoteDebounceTimer);
      this.quoteDebounceTimer = setTimeout(() => {
        this.quoteDebounceTimer = null;
        this.loadServices();
      }, 500);
    },

    formData() {
      console.log("formData", this.model);
      if (this.model.datetime == "ASAP") delete this.model.datetime;

      if (!this.model.voucher) delete this.model.voucher;

      if (!this.model.passenger_email) delete this.model.passenger_email;
    },

    async sendRequest() {
      this.formData();

      modelSchema
        .validate(this.model)
        .then(() => {
          this.sendingRequest = true;
          const est =
            this.selectedServiceObj &&
            Number.isInteger(this.selectedServiceObj.to_pay)
              ? this.selectedServiceObj.to_pay
              : null;
          if (this.model.payer === "BUSINESS" && est) {
            this.model.estimated_amount = est;
          } else {
            delete this.model.estimated_amount;
          }
          this.axios
            .post(this.$store.getters.getRootAPI + "booking", this.model)
            .then((response) => {
              this.$notify({
                title: "Done",
                text: "Order sent successfully",
                type: "success",
              });
              this.services = [];
              this.pick_up_location_obj = null;
              this.pull_down_location_obj = null;
              this.selectedTime = "ASAP";

              this.selectedService = "";
              this.selectedServiceObj = null;

              this.model.service_id = null;
              this.model.voucher = "";
              this.model.passenger_email = "";
              this.model.passenger_name = "";
              this.model.passenger_phone_number = "";

              this.$refs.tabs.addOrder(response.data.data);
            })
            .catch((error) => {
              const message =
                error.response &&
                error.response.data &&
                error.response.data.message;
              this.$notify({
                title: "Error",
                text: message || "Error",
                type: "error",
              });
            })
            .finally(() => {
              this.sendingRequest = false;
              window.scrollTo(0, 0);
            });
        })
        .catch(() => {
          window.scrollTo(0, 0);
          Object.keys(this.model).forEach((key) => {
            if (key in this.modelErrors) {
              modelSchema
                .validateAt(key, this.model)
                .then(() => {
                  this.modelErrors[key] = false;
                })
                .catch(() => {
                  console.log("VALIDATION ERROR AT ", key);
                  this.modelErrors[key] = true;
                });
            }
          });
        });
    },
  },
  watch: {
    "$store.getters.selectedLanguage": {
      immediate: true,
      handler(lang) {
        this.services = [];
        this.servicesOriginal.forEach((item) => {
          this.services.push({
            id: item.id,
            title: item["title" + this.primaryLang],
            img: item.img,
            estimated_price: item.estimated_price,
            to_pay: item.to_pay,
            discounted_amount: item.discounted_amount,
          });
        });

        if (this.services.length > 0) {
          this.selectedService = this.services[0].title;
          this.selectedServiceObj = this.services[0];
        }
      },
    },
    pick_up_location_obj: function (newVal) {
      if (newVal) this.model.pick_up_location_id = newVal.id;
      else this.model.pick_up_location_id = null;
      this.loadServices();
    },
    pull_down_location_obj: function (newVal) {
      if (newVal) this.model.pull_down_location_id = newVal.id;
      else this.model.pull_down_location_id = null;
      this.loadServices();
    },
    "model.voucher": function (newVal) {
      this.debouncedLoadServices();
    },
    selectedServiceObj: function (newVal) {
      if (newVal && newVal.id) this.model.service_id = newVal.id;
      else this.model.service_id = null;
    },
    selectedTime: function (newVal) {
      if (newVal !== "ASAP")
        this.model.datetime = moment(this.selectedTime).format("Y-MM-DD HH:mm");
      else this.model.datetime = "ASAP";
      this.loadServices();
    },
  },
};
</script>

<style lang="scss" scoped>
.payer-select {
  width: 100%;
  float: left;
  border: 1px solid #c1c5d8;
  border-radius: 8px;
  height: 44px;
  padding: 0 12px;
  background: #fff;
  font-size: 14px;
}

.balance-hint {
  width: 100%;
  float: left;
  margin-top: 6px;
  font-size: 13px;
  color: #788e98;
}

.limits-hint {
  width: 100%;
  float: left;
  margin-top: 4px;
  font-size: 12px;
  color: #788e98;
  line-height: 1.7;
}

.limits-hint > div {
  width: 100%;
  float: left;
}

.old-price {
  text-decoration: line-through;
  color: red;
}

.input {
  label {
    text-align: start;
    float: inline-start;
    font-size: 14px;
    padding-bottom: 6px;
    width: 100%;

    span {
      float: inline-end;
      text-align: end;
    }
  }

  input {
    float: inline-start;
    width: 100%;
    max-width: none;

    &[type="checkbox"] {
      width: 12px;
      height: 12px;
      float: inline-start;
      margin-right: 4px;
      margin-top: 4px;
    }

    &.error-border {
      border: 1px solid;
    }
  }
}

.estimation {
  label {
    text-align: start;
    float: inline-start;
    font-size: 14px;
    padding-bottom: 6px;
    width: 100%;
  }

  input {
    float: inline-start;
    width: 100%;
    max-width: none;

    &[type="checkbox"] {
      width: 12px;
      height: 12px;
      float: inline-start;
      margin-right: 4px;
      margin-top: 4px;
    }

    &.error-border {
      border: 1px solid;
    }
  }
}

.btn {
  width: 200px;
  max-width: 400px;
  border-radius: 5px;
}

button {
  max-width: 200px;
  max-height: 40px;

  span {
    color: white !important;
    width: 100%;
    text-align: center;
  }
}

.item {
  height: 100%;
  border-radius: 0.5rem;
  position: relative;
  background: white;
  float: inline-start;
  width: 70px;
  overflow: hidden;
  cursor: pointer;
  transition: 0.4s;
  flex-grow: 0;
  flex-shrink: 0;

  &.zero-width {
    width: 0px;
  }

  &.hidden {
    display: none;
  }

  &:hover {
    width: 230px;

    .item-container {
      .item-content:first-child {
        background-color: #f3f3f3;
      }
    }
  }

  .vehicle-counts {
    float: inline-start;
    height: 100%;
    padding: 6px;
    display: grid;
    grid-template-columns: 50% 50%;
    grid-template-rows: 33.3% 33.3% 33.3%;
    width: 160px;
    cursor: default;
    background: white;

    div {
      padding: 4px;
      overflow: hidden;
      width: 100%;

      label:first-child {
        font-weight: 600;
      }

      label {
        width: 100%;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .item-container {
    position: absolute;
    left: 0;
    top: 0;
    float: inline-start;
    display: flex;
    justify-content: flex-start;
    height: 100%;

    .item-content {
      float: inline-start;
      height: 100%;
      padding: 24px;
      width: 70px;
      text-align: center;
      background: white;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
      flex-direction: column;
      transition: 0.2s;

      h2 {
        font-size: 12pt;
        line-height: 16pt;
        max-width: 100%;
        // word-break: break-word;
      }

      label {
        font-size: 18pt;
        line-height: 24pt;
        max-width: 100%;
      }
    }
  }
}
</style>
